import ReplacementAssociateStudentModal from 'components/modal/evaluations/ReplacementAssociateStudentModal';
import {
  Button,
  Empty,
  Flex,
  Modal,
  Pagination,
  Skeleton,
  Typography
} from 'antd';
import { CrudFilters, HttpError, useDelete } from '@refinedev/core';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { ModalCreateAssignment } from './ModalCreateAssignment/ModalCreateAssignment';
import { ModalCreateEvaluation } from '../../TabEvaluations/components/ModalCreateEvaluation/ModalCreateEvaluation';
import { ReplacementGroup } from 'components/professor_area/ReplacementGroup';
import { TabAssignmentListItem } from './TabAssignmentsListItem';
import { useCallback, useMemo, useState } from 'react';
import { useCanMultiple } from 'components/layout/sider';
import { useModal, useModalForm, useSelect } from '@refinedev/antd';
import { useParams } from 'react-router-dom';
import type { IAssignment } from 'interfaces/assignments';
import type {
  IProfessorAreaEvaluationAssessmentCreate,
  IProfessorAreaEvaluationsList
} from 'interfaces/professor_area/evaluations';
import type { IProfessorAreaEvaluationAssignmentCreate } from 'interfaces/professor_area/assignments';
import type { TableProps } from 'antd/lib';

interface AssignmentItemProps {
  tableProps: TableProps<IProfessorAreaEvaluationsList>;
  onChangePage: (page: number) => void;
  refetch: () => void;
  disciplineId: string;
}

export const TabAssignmentsList = ({
  tableProps,
  onChangePage,
  refetch,
  disciplineId
}: AssignmentItemProps) => {
  const params = useParams();
  const classroomId = params.id!;
  const resource = 'professor_area/classroom_assignments';
  const meta = useMemo(() => ({ classroomId }), [classroomId]);
  const permissions = useCanMultiple([
    { resource, action: 'create' },
    { resource, action: 'show' },
    { resource, action: 'edit' },
    { resource, action: 'delete' },
    { resource: `${resource}/attempts`, action: 'list' },
    { resource: 'professor_area/classroom_enrollments', action: 'show' }
  ]);
  const canCreate = Boolean(permissions[0]?.data?.can);
  const canShow = Boolean(permissions[1]?.data?.can);
  const canEdit = Boolean(permissions[2]?.data?.can);
  const canDelete = Boolean(permissions[3]?.data?.can);
  const canShowEnrollments = Boolean(permissions[5]?.data?.can);
  // Bulk download exports the submissions, so it's gated on the same permission that
  // lists them (classroom_assignments/attempts:list), matching the backend bulk_download? policy.
  const canDownloadSubmissions = Boolean(permissions[4]?.data?.can);

  const { pagination, dataSource: items, loading } = tableProps;

  const { confirm } = Modal;

  const { mutateAsync: deleteEvaluation } = useDelete<IAssignment>();

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createModalShow,
    close: createModalClose,
    formLoading
  } = useModalForm<
    IProfessorAreaEvaluationAssignmentCreate,
    HttpError,
    IProfessorAreaEvaluationAssignmentCreate
  >({
    resource,
    meta,
    action: 'create',
    redirect: false,
    autoResetForm: true,
    onMutationSuccess: () => {
      onChangePage(1);
    },
    successNotification: () => ({
      type: 'success',
      message: 'Atividade cadastrada com sucesso',
      description: 'Sucesso!'
    }),
    errorNotification: () => ({
      type: 'error',
      message: 'Não foi possível cadastrar a atividade. Tente novamente!',
      description: 'Algo deu errado!'
    })
  });

  const [replacementParent, setReplacementParent] =
    useState<IProfessorAreaEvaluationsList | null>(null);

  const {
    modalProps: replacementModalProps,
    formProps: replacementFormProps,
    show: replacementModalShow,
    close: replacementModalCloseInternal,
    formLoading: replacementFormLoading
  } = useModalForm<
    IProfessorAreaEvaluationAssessmentCreate,
    HttpError,
    IProfessorAreaEvaluationAssessmentCreate
  >({
    resource,
    meta,
    action: 'create',
    redirect: false,
    autoResetForm: true,
    onMutationSuccess: () => {
      onChangePage(1);
    },
    successNotification: () => ({
      type: 'success',
      message: 'Substitutiva cadastrada com sucesso',
      description: 'Sucesso!'
    }),
    errorNotification: () => ({
      type: 'error',
      message: 'Não foi possível cadastrar a substitutiva. Tente novamente!',
      description: 'Algo deu errado!'
    })
  });

  const handleAddReplacement = useCallback(
    (assignment: IProfessorAreaEvaluationsList) => {
      setReplacementParent(assignment);
      replacementModalShow();
    },
    [replacementModalShow]
  );

  const replacementModalClose = useCallback(() => {
    replacementModalCloseInternal();
    setReplacementParent(null);
  }, [replacementModalCloseInternal]);

  const [associatingAssignment, setAssociatingAssignment] =
    useState<IProfessorAreaEvaluationsList | null>(null);

  const {
    show: associateModalShow,
    close: associateModalCloseInternal,
    modalProps: associateModalProps
  } = useModal();

  const handleAssociateStudents = useCallback(
    (assignment: IProfessorAreaEvaluationsList) => {
      setAssociatingAssignment(assignment);
      associateModalShow();
    },
    [associateModalShow]
  );

  const associateModalClose = useCallback(() => {
    associateModalCloseInternal();
    setAssociatingAssignment(null);
  }, [associateModalCloseInternal]);

  const filters: CrudFilters = [
    {
      field: 'filter[by_parent_type]',
      operator: 'eq',
      value: 'Graduate::Discipline'
    },
    { field: 'filter[by_parent_id]', operator: 'eq', value: disciplineId }
  ];

  const { selectProps: assignmentSelectProps } = useSelect<
    IAssignment,
    HttpError
  >({
    resource: 'assignments',
    optionLabel: 'name',
    optionValue: 'id',
    filters,
    onSearch: value => [
      { field: 'filter[search_name]', operator: 'eq', value }
    ],
    pagination: { mode: 'server' },
    queryOptions: {
      enabled: canCreate || canEdit
    }
  });

  const handleRemoveEvaluation = useCallback(
    (evaluationId: string) => {
      confirm({
        title: 'Deseja excluir essa atividade?',
        icon: <ExclamationCircleOutlined className="!text-red-500" />,
        content: 'Essa ação não pode ser revertida',
        okText: 'Excluir',
        cancelText: 'Voltar',
        okButtonProps: {
          danger: true
        },
        onOk() {
          deleteEvaluation({
            id: evaluationId,
            resource,
            meta,
            successNotification: () => ({
              type: 'success',
              description: 'Sucesso!',
              message: 'Atividade excluída com sucesso.'
            }),
            errorNotification: () => ({
              type: 'error',
              description: 'Ops!',
              message:
                'Não foi possível excluir esta atividade. Tente novamente.'
            })
          }).then(() => {
            refetch();
          });
        }
      });
    },
    [confirm, deleteEvaluation, meta, refetch, resource]
  );

  const hasData = useMemo(() => {
    return !loading && items && items?.length > 0;
  }, [items, loading]);

  if (loading) {
    return (
      <Skeleton active paragraph={{ rows: 6 }} className="w-full h-full" />
    );
  }

  const EmptyComponent = () => {
    return (
      <Flex
        vertical
        gap={16}
        justify="center"
        align="center"
        className="h-full"
      >
        <Empty description="Nenhuma atividade encontrada" />

        {canCreate && (
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => createModalShow()}
          >
            Adicionar atividade
          </Button>
        )}
      </Flex>
    );
  };

  const ContentComponent = () => {
    return (
      <>
        <Flex justify="space-between" align="center" className="mb-4">
          <Typography.Title level={5}>Atividades da turma</Typography.Title>
          {canCreate && (
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => createModalShow()}
            >
              Adicionar atividade
            </Button>
          )}
        </Flex>

        <Flex vertical>
          {items?.map(evaluation => (
            <ReplacementGroup
              key={evaluation.id}
              item={evaluation}
              renderItem={item => (
                <TabAssignmentListItem
                  item={item}
                  onRemove={handleRemoveEvaluation}
                  onAddReplacement={handleAddReplacement}
                  onAssociateStudents={handleAssociateStudents}
                  canShow={canShow}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  canCreate={canCreate}
                  canAssociateStudents={canShowEnrollments}
                  canDownloadSubmissions={canDownloadSubmissions}
                />
              )}
            />
          ))}
        </Flex>

        <Pagination
          className="justify-self-end mt-4"
          hideOnSinglePage
          {...pagination}
          onChange={onChangePage}
        />
      </>
    );
  };

  return (
    <>
      {hasData ? <ContentComponent /> : <EmptyComponent />}

      {createModalProps.open && (
        <ModalCreateAssignment
          modalProps={createModalProps}
          formProps={createFormProps}
          classroomId={classroomId}
          isLoading={formLoading}
          onCloseModal={createModalClose}
        />
      )}

      {replacementModalProps.open && replacementParent && (
        <ModalCreateEvaluation
          modalProps={replacementModalProps}
          formProps={replacementFormProps}
          classroomId={classroomId}
          isLoading={replacementFormLoading}
          onCloseModal={replacementModalClose}
          assessmentSelectProps={{ options: [] }}
          assignmentSelectProps={assignmentSelectProps}
          replacementParent={replacementParent}
        />
      )}

      {associateModalProps.open && associatingAssignment && (
        <ReplacementAssociateStudentModal
          modalProps={associateModalProps}
          modalClose={associateModalClose}
          parentId={associatingAssignment.id}
          resource="professor_area/classroom_enrollments"
          resourceMeta={{
            classroomId,
            pathTemplate: 'professor_area/classrooms/:classroomId/enrollments'
          }}
          enrollmentResource="professor_area/classroom_assignments"
          enrollmentResourceMeta={{
            classroomId,
            evaluationId: associatingAssignment.id,
            pathTemplate:
              'professor_area/classrooms/:classroomId/evaluations/:evaluationId/enrollments'
          }}
          optionLabel="student_name"
          searchFilterField="filter[by_user_name]"
        />
      )}
    </>
  );
};
